/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\UserBasicInfoSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Users module.
----------------------------------------------------------------*/


import { useState } from 'react';
import { User, AlertCircle } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { SensitiveField } from '../../../components/ui/PermissionField';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import LanguageSelect from '../../../components/ui/LanguageSelect';
import MenuItemSelect from '../../../components/ui/MenuItemSelect';
import { MODULE_SLUGS } from '../../../hooks/usePermissions';
import { SecretInput } from '../../../components/ui/SecretField';
import Toggle from '../../../components/ui/Toggle';

interface UserBasicInfoSectionProps {
    formData: {
        username: string;
        email: string;
        password: string;
        fullName: string;
        department: string;
        locale: string;
        timezone: string;
        startupDialog: string;
    };
    errors: Record<string, string>;
    mode: 'create' | 'edit';
    debugModeActive: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    finalMenuItems: any[];
    isLoadingMenuItems: boolean;
    onInputChange: (field: string, value: string) => void;
    t: (key: string) => string;
}

const fieldCls = (hasError: boolean) =>
    `px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${hasError ? 'border-red-500' : 'border-border'}`;

function FieldLabel({ label, debugKey, debugModeActive }: { label: string; debugKey: string; debugModeActive: boolean }) {
    return (
        <div className="flex flex-col">
            <label className="text-sm font-medium text-foreground">{label}</label>
            {debugModeActive && <span className="text-xs text-muted-foreground mt-0.5">{debugKey}</span>}
        </div>
    );
}

export function UserBasicInfoSection({ formData, errors, mode, debugModeActive, finalMenuItems, isLoadingMenuItems, onInputChange, t }: UserBasicInfoSectionProps) {
    const [changePassword, setChangePassword] = useState(false);
    return (
        <FormBox title={t('users.sections.basicInformation')} icon={<User className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* Username */}
                <SimpleTooltip content={t('users.tooltips.username')}>
                    <FieldLabel label={`${t('users.username')} *`} debugKey="username" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <div className="flex flex-col">
                    <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => onInputChange('username', e.target.value)}
                        className={fieldCls(!!errors.username)}
                        placeholder={t('users.enterUsername')}
                    />
                    {errors.username && (
                        <span className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.username}
                        </span>
                    )}
                </div>

                {/* Email */}
                <SimpleTooltip content={t('users.tooltips.email')}>
                    <FieldLabel label={`${t('users.email')} *`} debugKey="email" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <div className="flex flex-col">
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => onInputChange('email', e.target.value)}
                        className={fieldCls(!!errors.email)}
                        placeholder={t('users.enterEmail')}
                    />
                    {errors.email && (
                        <span className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.email}
                        </span>
                    )}
                </div>

                {/* Password */}
                <SimpleTooltip content={t('users.tooltips.password')}>
                    <FieldLabel label={`${t('users.password')}${mode === 'create' ? ' *' : ''}`} debugKey="password" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <div className="flex flex-col gap-2">
                    {mode === 'edit' && (
                        <div className="flex items-center gap-3">
                            <Toggle
                                id="changePassword"
                                value={changePassword}
                                onChange={(checked) => {
                                    setChangePassword(checked);
                                    if (!checked) {
                                        onInputChange('password', '');
                                    }
                                }}
                            />
                            <span 
                                className="text-sm text-muted-foreground cursor-pointer select-none" 
                                onClick={() => {
                                    const checked = !changePassword;
                                    setChangePassword(checked);
                                    if (!checked) {
                                        onInputChange('password', '');
                                    }
                                }}
                            >
                                {t('users.changePassword')}
                            </span>
                        </div>
                    )}
                    {(mode === 'create' || changePassword) && (
                        <SensitiveField moduleId={MODULE_SLUGS.USERS}>
                            <SecretInput
                                value={formData.password}
                                onChange={(value) => onInputChange('password', value)}
                                className={fieldCls(!!errors.password)}
                                placeholder={mode === 'create' ? 'Enter password' : t('users.enterNewPassword')}
                                showToggle={true}
                            />
                        </SensitiveField>
                    )}
                    {errors.password && (
                        <span className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.password}
                        </span>
                    )}
                </div>

                {/* Full Name */}
                <SimpleTooltip content={t('users.tooltips.fullName')}>
                    <FieldLabel label={t('users.fullName')} debugKey="full_name" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => onInputChange('fullName', e.target.value)}
                    className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                    placeholder={t('users.enterFullName')}
                />

                {/* Department */}
                <SimpleTooltip content={t('users.tooltips.department')}>
                    <FieldLabel label={t('users.department')} debugKey="department" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => onInputChange('department', e.target.value)}
                    className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                    placeholder={t('users.enterDepartment')}
                />

                {/* Locale */}
                <SimpleTooltip content={t('users.tooltips.locale')}>
                    <FieldLabel label={t('users.locale')} debugKey="locale" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <LanguageSelect 
                    value={formData.locale} 
                    onChange={(value) => onInputChange('locale', value)} 
                    placeholder={t('users.selectLanguage') + ' *'}
                    className={!formData.locale ? '!border-red-500/50 !bg-red-500/5 shadow-[0_0_0_1px_rgba(239,68,68,0.3)]' : ''} 
                />

                {/* Timezone */}
                <SimpleTooltip content={t('users.tooltips.timezone')}>
                    <FieldLabel label={t('users.timezone')} debugKey="timezone" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <TimezoneSelect value={formData.timezone} onChange={(value) => onInputChange('timezone', value)} placeholder="Select timezone..." />

                {/* Startup Dialog */}
                <SimpleTooltip content={t('users.tooltips.startupDialog')}>
                    <FieldLabel label={t('users.startupDialog')} debugKey="startup_dialog" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <MenuItemSelect
                    value={formData.startupDialog}
                    onChange={(value) => onInputChange('startupDialog', value)}
                    options={finalMenuItems}
                    loading={isLoadingMenuItems}
                    placeholder={t('users.selectStartupDialog') + ' *'}
                    className={!formData.startupDialog ? '!border-red-500/50 !bg-red-500/5 shadow-[0_0_0_1px_rgba(239,68,68,0.3)]' : ''}
                />
            </div>
        </FormBox>
    );
}
